/* eslint-disable */
/**
 * stateAtomicDemo.js — three-page demo with every smart element type.
 *
 * Animation hierarchy — every element supports up to three layers,
 * and every layer accepts boxAnimation (entry) + boxExitAnimation (exit):
 *
 *   1. Element-level (whole atomic box; always present):
 *        element.boxAnimation     — whole-box entry
 *        element.boxExitAnimation — whole-box exit (optional)
 *      Uses normal box presets (Slide, Fade, Scale, Bounce, …).
 *      Type Writer / Wavy Text only when the element is intrinsically
 *      text-only (eyebrow, heading, footer).
 *
 *   2. Sub-field (text leaves like title/body/eyebrow/subtext/…):
 *        <field>.boxAnimation
 *        <field>.boxExitAnimation
 *      Type Writer / Wavy Text usable alongside normal presets.
 *
 *   3. Item-level (when items[i] / steps[i] / stats[i] / left|right is a box):
 *        item.boxAnimation
 *        item.boxExitAnimation
 *      Normal presets only; the box's inner fields stay plain.
 *
 * Animation payload shape mirrors what UPDATE_BOX_ANIMATION writes to
 * Redux box.boxAnimation:
 *   { animation: <full preset {name, animation:{initial,animate,transition}}>,
 *     duration, delay }
 * `wrapEntry` / `wrapExit` look the preset up from animationHelpers so we
 * don't repeat the nested spec inline.
 *
 * Boots via `?atomicDemo=1` (wired in `_editor/containers/ReduxProvider.jsx`).
 */

import IdealStudio from "../core/editor/main";
import {
  getBoxAnimationPresets,
  getBoxExitAnimationPresets,
} from "../_editor/utils/animationHelpers";

function encState(elementData, cssVariables) {
  return encodeURIComponent(
    JSON.stringify({
      elementData,
      cssVariables: cssVariables || {},
      themeId: "midnight-pro",
      paletteId: "electric-blue",
    })
  );
}

// Build a boxAnimation spec matching the production Redux shape — preset
// object (with nested initial/animate/transition) under `animation`.
function wrapEntry(name, duration, delay = 0) {
  const preset = getBoxAnimationPresets().find((p) => p.name === name);
  return {
    animation: preset ?? { name },
    duration,
    delay,
  };
}

function wrapExit(name, duration, delay = 0) {
  const preset = getBoxExitAnimationPresets().find((p) => p.name === name);
  return {
    animation: preset ?? { name },
    duration,
    delay,
  };
}

const PAGE_1 = "pa-atomic-demo-01";
const PAGE_2 = "pa-atomic-demo-02";
const PAGE_3 = "pa-atomic-demo-03";

// ═══ PAGE 1 — UI primitives ═══════════════════════════════════════════════════

// Text-only elements: no sub-elements, animation lives directly on the element.
const eyebrowElement = {
  type: "eyebrow",
  text: "Module 01 · Introduction",
  boxAnimation: wrapEntry("Type Writer", 1.2, 0),
};

const headingElement = {
  type: "heading",
  text: "Build the Future of E-Learning",
  boxAnimation: wrapEntry("Wavy Text", 1.5, 0.2),
  boxExitAnimation: wrapExit("Fade Out", 0.4, 5),
};

const footerElement = {
  type: "footer",
  text: "© 2026 Aidea Studio",
  boxAnimation: wrapEntry("Type Writer", 1, 0.3),
};

const dividerElement = {
  type: "divider",
  // Element-level: whole divider
  boxAnimation: wrapEntry("Fade In", 0.5, 0),
  boxExitAnimation: wrapExit("Fade Out", 0.4, 5),
  // Sub-field: label text
  label: {
    text: "Section Break",
    boxAnimation: wrapEntry("Type Writer", 0.8, 0.3),
    boxExitAnimation: wrapExit("Type Writer Out", 0.6, 4.5),
  },
};

const badgeElement = {
  type: "badge",
  // Badge is a box; element-level box anim covers it.
  boxAnimation: wrapEntry("Scale In", 0.5, 0),
  boxExitAnimation: wrapExit("Scale Out", 0.5, 5),
  text: "New",
};

const ctaButtonElement = {
  type: "cta-button",
  // Element-level: whole cta block (button + subtext)
  boxAnimation: wrapEntry("Slide In Up", 0.5, 0),
  boxExitAnimation: wrapExit("Slide Out Down", 0.5, 6),
  // Sub-field: button itself is a box
  label: {
    text: "Start Learning",
    boxAnimation: wrapEntry("Scale In", 0.6, 0.4),
    boxExitAnimation: wrapExit("Scale Out", 0.5, 5.5),
  },
  href: "#",
  // Sub-field: subtext is text — Type Writer / Wavy Text usable
  subtext: {
    text: "Free, no credit card",
    boxAnimation: wrapEntry("Wavy Text", 1.5, 1.2),
    boxExitAnimation: wrapExit("Wavy Text Out", 1, 5),
  },
};

// ═══ PAGE 2 — Narrative / Layout ══════════════════════════════════════════════

const heroElement = {
  type: "hero",
  // Element-level: whole hero
  boxAnimation: wrapEntry("Fade In", 0.6, 0),
  boxExitAnimation: wrapExit("Slide Out Up", 0.5, 7),
  // Sub-field text leaves
  eyebrow: {
    text: "AI-Powered Authoring",
    boxAnimation: wrapEntry("Type Writer", 1, 0.2),
    boxExitAnimation: wrapExit("Type Writer Out", 0.8, 5),
  },
  heading: {
    text: "Build the Future of E-Learning",
    boxAnimation: wrapEntry("Slide In Up", 0.7, 0.5),
    boxExitAnimation: wrapExit("Scale Out", 0.5, 6),
  },
  body: {
    text: "An intelligent authoring environment that helps you design engaging, interactive courses in minutes — powered by AI.",
    boxAnimation: wrapEntry("Wavy Text", 1.8, 0.9),
    boxExitAnimation: wrapExit("Wavy Text Out", 1, 5.5),
  },
  // Sub-field: badge is a box → normal preset only
  badge: {
    text: "New",
    boxAnimation: wrapEntry("Scale In", 0.4, 1.2),
    boxExitAnimation: wrapExit("Scale Out", 0.4, 5.7),
  },
};

const sectionElement = {
  type: "section",
  boxAnimation: wrapEntry("Fade In", 0.5, 0),
  boxExitAnimation: wrapExit("Fade Out", 0.5, 6),
  sectionNumber: {
    text: "01",
    boxAnimation: wrapEntry("Scale In", 0.5, 0.2),
    boxExitAnimation: wrapExit("Scale Out", 0.4, 5.5),
  },
  heading: {
    text: "Why It Matters",
    boxAnimation: wrapEntry("Type Writer", 1, 0.5),
    boxExitAnimation: wrapExit("Type Writer Out", 0.8, 5),
  },
  body: {
    text: "Learners absorb structured, themed content far better than raw text dumps.",
    boxAnimation: wrapEntry("Wavy Text", 1.8, 0.9),
    boxExitAnimation: wrapExit("Wavy Text Out", 1, 4.8),
  },
};

const iconBlockElement = {
  type: "icon-block",
  icon: "star",
  iconUrl: null,
  iconSize: 48,
  layout: "row",
  boxAnimation: wrapEntry("Scale In", 0.6, 0),
  boxExitAnimation: wrapExit("Scale Out", 0.5, 6),
  eyebrow: {
    text: "Feature",
    boxAnimation: wrapEntry("Wavy Text", 1.2, 0.4),
    boxExitAnimation: wrapExit("Wavy Text Out", 0.8, 5),
  },
  title: {
    text: "Smart Suggestions",
    boxAnimation: wrapEntry("Type Writer", 1, 0.7),
    boxExitAnimation: wrapExit("Type Writer Out", 0.8, 5.3),
  },
  body: {
    text: "Drop in a topic — get a fully-themed scene back.",
    boxAnimation: wrapEntry("Slide In Up", 0.5, 1),
    boxExitAnimation: wrapExit("Slide Out Down", 0.5, 5.5),
  },
};

const quoteElement = {
  type: "quote",
  boxAnimation: wrapEntry("Fade In", 0.5, 0),
  boxExitAnimation: wrapExit("Fade Out", 0.5, 6),
  quote: {
    text: "Great pedagogy starts with great structure.",
    boxAnimation: wrapEntry("Type Writer", 1.6, 0.3),
    boxExitAnimation: wrapExit("Type Writer Out", 1.2, 5),
  },
  attribution: {
    text: "Maria López",
    boxAnimation: wrapEntry("Slide In Up", 0.5, 1.3),
    boxExitAnimation: wrapExit("Slide Out Down", 0.5, 5.3),
  },
  role: {
    text: "Learning Designer",
    boxAnimation: wrapEntry("Wavy Text", 1.4, 1.6),
    boxExitAnimation: wrapExit("Wavy Text Out", 1, 5.5),
  },
};

const twoColumnElement = {
  type: "two-column",
  boxAnimation: wrapEntry("Fade In", 0.5, 0),
  boxExitAnimation: wrapExit("Fade Out", 0.5, 7),
  eyebrow: {
    text: "At a glance",
    boxAnimation: wrapEntry("Type Writer", 0.8, 0.2),
    boxExitAnimation: wrapExit("Type Writer Out", 0.6, 5),
  },
  heading: {
    text: "Two Pillars",
    boxAnimation: wrapEntry("Wavy Text", 1.4, 0.5),
    boxExitAnimation: wrapExit("Wavy Text Out", 1, 5.3),
  },
  // Legacy flat shape — renderer also accepts:
  // columns: [ {title, body, bullets}, {title, body, bullets} ],
  left: {
    title: {
      text: "Theme",
      boxAnimation: wrapEntry("Slide In Left", 0.5, 0.8),
      boxExitAnimation: wrapExit("Slide Out Left", 0.5, 5.5),
    },
    body: {
      text: "Color tokens, type scale, motion — all in one file.",
      boxAnimation: wrapEntry("Type Writer", 1.2, 1.1),
      boxExitAnimation: wrapExit("Type Writer Out", 0.8, 5.7),
    },
    bullets: [
      {
        text: "Tokens",
        boxAnimation: wrapEntry("Wavy Text", 1, 1.4),
        boxExitAnimation: wrapExit("Wavy Text Out", 0.8, 5.9),
      },
      { text: "Scales", boxAnimation: wrapEntry("Wavy Text", 1, 1.6) },
      { text: "Motion", boxAnimation: wrapEntry("Wavy Text", 1, 1.8) },
    ],
  },
  right: {
    title: {
      text: "Composition",
      boxAnimation: wrapEntry("Slide In Right", 0.5, 0.8),
      boxExitAnimation: wrapExit("Slide Out Right", 0.5, 5.5),
    },
    body: {
      text: "Mix-and-match smart elements; lay them out without HTML.",
      boxAnimation: wrapEntry("Wavy Text", 1.5, 1.1),
      boxExitAnimation: wrapExit("Wavy Text Out", 1, 5.7),
    },
    bullets: [
      {
        text: "Elements",
        boxAnimation: wrapEntry("Type Writer", 0.8, 1.4),
        boxExitAnimation: wrapExit("Type Writer Out", 0.6, 5.9),
      },
      { text: "Layouts", boxAnimation: wrapEntry("Type Writer", 0.8, 1.6) },
      { text: "Reorder", boxAnimation: wrapEntry("Type Writer", 0.8, 1.8) },
    ],
  },
};

const comparisonElement = {
  type: "comparison",
  boxAnimation: wrapEntry("Fade In", 0.5, 0),
  boxExitAnimation: wrapExit("Fade Out", 0.5, 6.5),
  eyebrow: {
    text: "Old vs New",
    boxAnimation: wrapEntry("Type Writer", 0.8, 0.2),
    boxExitAnimation: wrapExit("Type Writer Out", 0.6, 5),
  },
  heading: {
    text: "Before & After Aidea",
    boxAnimation: wrapEntry("Wavy Text", 1.4, 0.5),
    boxExitAnimation: wrapExit("Wavy Text Out", 1, 5.3),
  },
  // Legacy flat shape — renderer also accepts:
  // columns: [ {title, body, bullets, badge}, {title, body, bullets, badge} ],
  // Each column is an item-level box → box anim only; inner fields plain.
  left: {
    boxAnimation: wrapEntry("Slide In Left", 0.6, 1),
    boxExitAnimation: wrapExit("Slide Out Left", 0.5, 5.5),
    title: "Before",
    body: "Hand-craft every slide in a deck.",
    bullets: ["Slow authoring", "Inconsistent theme", "Hard to remix"],
    badge: "Old way",
  },
  right: {
    boxAnimation: wrapEntry("Slide In Right", 0.6, 1.3),
    boxExitAnimation: wrapExit("Slide Out Right", 0.5, 5.7),
    title: "After",
    body: "AI drafts; you direct.",
    bullets: ["AI-drafted scenes", "Theme-aware tokens", "Composable elements"],
    badge: "Recommended",
  },
};

// ═══ PAGE 3 — Lists & Data ════════════════════════════════════════════════════

const bulletListElement = {
  type: "bullet-list",
  boxAnimation: wrapEntry("Fade In", 0.5, 0),
  boxExitAnimation: wrapExit("Fade Out", 0.5, 6),
  title: {
    text: "Why Choose Aidea",
    boxAnimation: wrapEntry("Type Writer", 1, 0.2),
    boxExitAnimation: wrapExit("Type Writer Out", 0.8, 5),
  },
  items: [
    {
      text: "AI-drafted scenes",
      boxAnimation: wrapEntry("Wavy Text", 1, 0.7),
      boxExitAnimation: wrapExit("Wavy Text Out", 0.8, 5.2),
    },
    { text: "Theme-aware rendering", boxAnimation: wrapEntry("Type Writer", 1, 1) },
    { text: "Field-level animations", boxAnimation: wrapEntry("Slide In Left", 0.4, 1.3) },
    {
      text: "Export to SCORM / xAPI",
      boxAnimation: wrapEntry("Wavy Text", 1, 1.6),
      boxExitAnimation: wrapExit("Wavy Text Out", 0.8, 5.4),
    },
  ],
};

const cardGridElement = {
  type: "card-grid",
  boxAnimation: wrapEntry("Fade In", 0.5, 0),
  boxExitAnimation: wrapExit("Fade Out", 0.5, 6),
  title: {
    text: "Building Blocks",
    boxAnimation: wrapEntry("Type Writer", 1, 0.2),
    boxExitAnimation: wrapExit("Type Writer Out", 0.8, 5),
  },
  columns: 3,
  items: [
    {
      boxAnimation: wrapEntry("Scale In", 0.5, 0.7),
      boxExitAnimation: wrapExit("Scale Out", 0.5, 5.5),
      title: "Define",
      body: "Goals + audience.",
      badge: "Step 1",
    },
    {
      boxAnimation: wrapEntry("Scale In", 0.5, 0.9),
      boxExitAnimation: wrapExit("Scale Out", 0.5, 5.7),
      title: "Generate",
      body: "AI drafts the scene.",
      badge: "Step 2",
    },
    {
      boxAnimation: wrapEntry("Scale In", 0.5, 1.1),
      boxExitAnimation: wrapExit("Scale Out", 0.5, 5.9),
      title: "Refine",
      body: "Adjust the design.",
      badge: "Step 3",
    },
  ],
};

const timelineElement = {
  type: "timeline",
  boxAnimation: wrapEntry("Fade In", 0.5, 0),
  boxExitAnimation: wrapExit("Fade Out", 0.5, 6.5),
  title: {
    text: "How It Works",
    boxAnimation: wrapEntry("Wavy Text", 1.4, 0.2),
    boxExitAnimation: wrapExit("Wavy Text Out", 1, 5),
  },
  orientation: "horizontal",
  steps: [
    {
      boxAnimation: wrapEntry("Slide In Left", 0.5, 0.7),
      boxExitAnimation: wrapExit("Slide Out Left", 0.5, 5.4),
      title: "Define",
      body: "Describe your learning objectives and target audience.",
    },
    {
      boxAnimation: wrapEntry("Slide In Left", 0.5, 1),
      boxExitAnimation: wrapExit("Slide Out Left", 0.5, 5.6),
      title: "Generate",
      body: "AI drafts a structured, themed scene from your prompt.",
    },
    {
      boxAnimation: wrapEntry("Slide In Left", 0.5, 1.3),
      boxExitAnimation: wrapExit("Slide Out Left", 0.5, 5.8),
      title: "Refine",
      body: "Edit boxes, swap elements, and adjust the design.",
    },
    {
      boxAnimation: wrapEntry("Slide In Left", 0.5, 1.6),
      boxExitAnimation: wrapExit("Slide Out Right", 0.5, 6),
      title: "Publish",
      body: "Export to SCORM, xAPI, or embed in any LMS.",
    },
  ],
};

const statGroupElement = {
  type: "stat-group",
  boxAnimation: wrapEntry("Fade In", 0.5, 0),
  boxExitAnimation: wrapExit("Fade Out", 0.5, 6),
  title: {
    text: "Impact at Scale",
    boxAnimation: wrapEntry("Type Writer", 1, 0.2),
    boxExitAnimation: wrapExit("Type Writer Out", 0.8, 5),
  },
  stats: [
    {
      boxAnimation: wrapEntry("Scale In", 0.5, 0.7),
      boxExitAnimation: wrapExit("Scale Out", 0.5, 5.3),
      value: "100",
      unit: "k",
      label: "Active users",
      detail: "monthly",
    },
    {
      boxAnimation: wrapEntry("Scale In", 0.5, 1),
      boxExitAnimation: wrapExit("Scale Out", 0.5, 5.5),
      value: "50",
      unit: "+",
      label: "Themed templates",
      detail: "and growing",
    },
    {
      boxAnimation: wrapEntry("Scale In", 0.5, 1.3),
      boxExitAnimation: wrapExit("Scale Out", 0.5, 5.7),
      value: "98",
      unit: "%",
      label: "Completion rate",
      detail: "industry avg 62%",
    },
  ],
};

// ─── Box / toolbar factories ──────────────────────────────────────────────────

let _boxCounter = 0;
function nextBoxId(prefix) {
  _boxCounter += 1;
  return `bo-atomic-${prefix}-${_boxCounter.toString().padStart(2, "0")}`;
}

function makeBox(id, parentId, x, y, boxAnimation, boxExitAnimation) {
  return {
    id,
    parent: parentId,
    container: 0,
    level: 0,
    col: 0,
    row: 0,
    position: { x: `${x}%`, y: `${y}%`, type: "absolute" },
    content: {},
    draggable: true,
    resizable: true,
    showTextEditor: false,
    fragment: {},
    children: [],
    sortableContainers: {},
    containedViews: [],
    boxAnimation: boxAnimation ?? null,
    boxExitAnimation: boxExitAnimation ?? null,
  };
}

function makeToolbar(id, elementData, x, y, width, height) {
  return {
    id,
    pluginId: "AtomicBoxPlugin",
    state: { __text: encState(elementData) },
    structure: {
      width,
      widthUnit: "%",
      height,
      heightUnit: "px",
      rotation: 0,
      aspectRatio: false,
      position: "absolute",
      x: `${x}%`,
      y: `${y}%`,
      zIndex: 1,
      isContentElement: true,
    },
    style: {
      padding: 0,
      backgroundColor: "transparent",
      borderWidth: 0,
      borderStyle: "solid",
      borderColor: "transparent",
      borderRadius: 0,
      opacity: 1,
    },
    showTextEditor: false,
  };
}

// ─── Page layouts ─────────────────────────────────────────────────────────────
// [elementData, x%, y%, width%, height_px, idPrefix]

const PAGE_1_LAYOUT = [
  [eyebrowElement, 4, 3, 50, 50, "eye"],
  [headingElement, 4, 14, 92, 100, "head"],
  [footerElement, 4, 32, 50, 50, "foot"],
  [dividerElement, 4, 44, 92, 60, "div"],
  [badgeElement, 4, 60, 20, 50, "badge"],
  [ctaButtonElement, 30, 60, 35, 60, "cta"],
];

const PAGE_2_LAYOUT = [
  [heroElement, 4, 3, 92, 180, "hero"],
  [sectionElement, 4, 33, 45, 130, "sec"],
  [iconBlockElement, 52, 33, 44, 130, "ico"],
  [quoteElement, 4, 56, 45, 130, "quote"],
  [twoColumnElement, 52, 56, 44, 130, "tcol"],
];

const PAGE_3_LAYOUT = [
  [bulletListElement, 4, 3, 45, 240, "bl"],
  [cardGridElement, 52, 3, 44, 240, "cg"],
  [timelineElement, 4, 42, 92, 160, "tl"],
  [statGroupElement, 4, 70, 45, 160, "stat"],
  [comparisonElement, 52, 70, 44, 160, "comp"],
];

// ─── Build state ──────────────────────────────────────────────────────────────

function buildPage(layout, pageId, base) {
  const boxes = {};
  const toolbars = {};
  const boxIds = [];

  for (const [elementData, x, y, w, h, prefix] of layout) {
    const id = nextBoxId(prefix);
    // Lift element-level animation onto Redux box; keep elementData clean.
    const { boxAnimation, boxExitAnimation, ...elementDataInner } = elementData;
    boxes[id] = makeBox(id, pageId, x, y, boxAnimation, boxExitAnimation);
    toolbars[id] = makeToolbar(id, elementDataInner, x, y, w, h);
    boxIds.push(id);
  }

  const defaultPageId = base.undoGroup.present.navItemsIds[0];
  const defaultViewToolbar = base.undoGroup.present.viewToolbarsById[defaultPageId] || {};
  const defaultExercises = base.undoGroup.present.exercises?.[defaultPageId] || { exercises: {} };

  return {
    navItem: {
      id: pageId,
      isExpanded: true,
      parent: 0,
      linkedBoxes: {},
      children: [],
      boxes: boxIds,
      level: 1,
      type: "slide",
      hidden: false,
      extraFiles: {},
      customSize: 0,
    },
    boxes,
    toolbars,
    viewToolbar: {
      ...defaultViewToolbar,
      id: pageId,
      documentTitleContent: `Page ${pageId.slice(-2)}`,
    },
    exercises: { ...defaultExercises, id: pageId, exercises: {} },
  };
}

function buildState() {
  const base = IdealStudio.InitialState;
  const present = base.undoGroup.present;

  const page1 = buildPage(PAGE_1_LAYOUT, PAGE_1, base);
  const page2 = buildPage(PAGE_2_LAYOUT, PAGE_2, base);
  const page3 = buildPage(PAGE_3_LAYOUT, PAGE_3, base);

  return {
    ...base,
    undoGroup: {
      ...base.undoGroup,
      present: {
        ...present,
        navItemsIds: [PAGE_1, PAGE_2, PAGE_3],
        navItemSelected: PAGE_1,
        indexSelected: PAGE_1,
        navItemsById: {
          0: { ...present.navItemsById[0], children: [PAGE_1, PAGE_2, PAGE_3] },
          [PAGE_1]: page1.navItem,
          [PAGE_2]: page2.navItem,
          [PAGE_3]: page3.navItem,
        },
        boxesById: { ...page1.boxes, ...page2.boxes, ...page3.boxes },
        pluginToolbarsById: { ...page1.toolbars, ...page2.toolbars, ...page3.toolbars },
        viewToolbarsById: {
          [PAGE_1]: page1.viewToolbar,
          [PAGE_2]: page2.viewToolbar,
          [PAGE_3]: page3.viewToolbar,
        },
        exercises: {
          [PAGE_1]: page1.exercises,
          [PAGE_2]: page2.exercises,
          [PAGE_3]: page3.exercises,
        },
      },
    },
  };
}

const stateAtomicDemo = buildState();
export default stateAtomicDemo;
